/**
 * Artifact type utilities for type-safe pipeline data flow.
 * 
 * Provides color mapping, labels, and type checking for artifact types.
 */

/**
 * Artifact types that can flow between processors
 */
export type ArtifactType = 'string' | 'file' | 'json' | 'list' | 'vector_db';

/**
 * Color mapping for artifact types (for connector handles and edges)
 */
export const ARTIFACT_TYPE_COLORS: Record<ArtifactType, string> = {
  string: '#3b82f6',    // Blue (default)
  file: '#8b5cf6',      // Purple
  json: '#06b6d4',      // Cyan
  list: '#10b981',      // Green
  vector_db: '#f59e0b', // Amber (stands out for context-only types!)
};

/**
 * Human-readable labels for artifact types
 */
export const ARTIFACT_TYPE_LABELS: Record<ArtifactType, string> = {
  string: 'Text',
  file: 'File',
  json: 'JSON',
  list: 'List',
  vector_db: 'Vector DB',
};

/**
 * Get the color for an artifact type
 * 
 * @param type - Artifact type (defaults to 'string' if undefined)
 * @returns Hex color code
 */
export function getArtifactTypeColor(type: string | undefined): string {
  if (!type) return ARTIFACT_TYPE_COLORS.string;
  return ARTIFACT_TYPE_COLORS[type as ArtifactType] || ARTIFACT_TYPE_COLORS.string;
}

/**
 * Get the human-readable label for an artifact type
 * 
 * @param type - Artifact type (defaults to 'string' if undefined)
 * @returns Human-readable label
 */
export function getArtifactTypeLabel(type: string | undefined): string {
  if (!type) return ARTIFACT_TYPE_LABELS.string;
  return ARTIFACT_TYPE_LABELS[type as ArtifactType] || ARTIFACT_TYPE_LABELS.string;
}

/**
 * Check if an artifact type is context-only (not inserted into prompts)
 * 
 * Context-only types are loaded into processor context and should not
 * be checked for prompt variable usage by the validator.
 * 
 * @param type - Artifact type
 * @returns True if type is context-only
 */
export function isContextOnlyType(type: string | undefined): boolean {
  return type === 'vector_db';
}

/**
 * Check if an artifact type is prompt-compatible (inserted into prompts)
 * 
 * @param type - Artifact type
 * @returns True if type is prompt-compatible
 */
export function isPromptType(type: string | undefined): boolean {
  return !isContextOnlyType(type);
}

/**
 * Get artifact type from output_types mapping
 * 
 * @param outputTypes - Processor's output_types mapping
 * @param outputKey - Output key name
 * @returns Artifact type (defaults to 'string')
 */
export function getOutputType(
  outputTypes: Record<string, string> | undefined,
  outputKey: string
): string {
  if (!outputTypes) return 'string';
  return outputTypes[outputKey] || 'string';
}

/**
 * Get badge CSS class for dynamic output indicator.
 * 
 * @returns CSS class for "⚡ dynamic" badge
 */
export function getDynamicOutputBadgeClass(): string {
  return 'text-purple-600 dark:text-purple-400';
}

